import * as XLSX from "xlsx-js-style";
import { PRIORITIES, STATUS_LABELS } from "./constants";
import { htmlToText } from "./richText";

/**
 * Excel export of one board column (same xlsx-js-style approach and orange
 * header as the HR form export). Exports exactly the tasks shown in the
 * column, i.e. with the board's active filters applied.
 */

// Columns that offer an export button.
export const EXPORTABLE_STATUSES = ["product_backlog", "sprint_backlog", "done"];

const HEADERS = [
  "#",
  "Title",
  "Type",
  "Status",
  "Priority",
  "Projects",
  "Squad",
  "Sprint",
  "Assignees",
  "Due date",
  "Deadline",
  "QA rejections",
  "Created",
  "Updated",
  "Description",
];
const WIDTHS = [7, 40, 7, 15, 10, 20, 18, 18, 22, 12, 16, 13, 12, 12, 60];

const day = (v) => (v ? new Date(v).toISOString().slice(0, 10) : "");
const nameOf = (lookup, id) => (id && lookup[id] ? lookup[id].name : "");

/** Spreadsheet rows (header first) for the given tasks. Pure, for tests. */
export const columnRows = (tasks, lookups) => [
  HEADERS,
  ...tasks.map((t) => [
    t.number ?? "",
    t.title || "",
    t.taskType || "",
    STATUS_LABELS[t.status] || t.status,
    PRIORITIES.find((p) => p.value === t.priority)?.label || "",
    (t.projects || []).map((id) => nameOf(lookups.projects, id)).filter(Boolean).join(", "),
    nameOf(lookups.squads, t.squad),
    nameOf(lookups.sprints, t.sprint),
    (t.assignees || []).map((a) => a.fullNameEn || a.nickname).filter(Boolean).join(", "),
    day(t.dueDate),
    t.deadlineMissed ? `Late ${t.daysLate || 1}d` : t.dueDate ? "On time" : "",
    t.qaRejectedCount || 0,
    day(t.createdAt),
    day(t.updatedAt),
    htmlToText(t.description || ""),
  ]),
];

const border = {
  top: { style: "thin", color: { rgb: "CCCCCC" } },
  bottom: { style: "thin", color: { rgb: "CCCCCC" } },
  left: { style: "thin", color: { rgb: "CCCCCC" } },
  right: { style: "thin", color: { rgb: "CCCCCC" } },
};
const headerStyle = {
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11, name: "Calibri" },
  fill: { fgColor: { rgb: "E68005" }, patternType: "solid" },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border,
};
const cellStyle = {
  font: { color: { rgb: "333333" }, sz: 10, name: "Calibri" },
  alignment: { horizontal: "left", vertical: "top", wrapText: true },
  border,
};
const altStyle = { ...cellStyle, fill: { fgColor: { rgb: "F9F9F9" }, patternType: "solid" } };

/** Build and download `<Status>[-<Sprint>]-<date>.xlsx`. */
export const exportColumnToExcel = ({ status, tasks, lookups, sprintName }) => {
  const rows = columnRows(tasks, lookups);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell) cell.s = r === 0 ? headerStyle : r % 2 === 0 ? altStyle : cellStyle;
    }
  }
  ws["!cols"] = WIDTHS.map((wch) => ({ wch }));
  ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomLeft" };

  const label = STATUS_LABELS[status] || status;
  const wb = XLSX.utils.book_new();
  // Sheet names are limited to 31 characters.
  XLSX.utils.book_append_sheet(wb, ws, label.slice(0, 31));
  const safe = (s) => s.replace(/[^\w-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  const parts = [safe(label), sprintName ? safe(sprintName) : null, new Date().toISOString().slice(0, 10)];
  XLSX.writeFile(wb, `${parts.filter(Boolean).join("_")}.xlsx`);
};
